import React, { useState, useEffect } from "react";
import Carousel from "react-multi-carousel";
import { Link } from "react-router-dom";
import "react-multi-carousel/lib/styles.css";
import { useTheme } from "../../context/ThemeContext";
import { motion } from "framer-motion";

const CustomDot = ({ onClick, active }) => (
  <button
    onClick={onClick}
    type="button"
    className={`mx-0.5 transition-all duration-300 focus:outline-none rounded-full ${
      active
        ? "w-5 h-1.5 bg-gray-600 rounded-sm"
        : "w-1.5 h-1.5 bg-gray-400 hover:bg-gray-500 rounded-full"
    }`}
  />
);

const LeftArrow = ({ onClick, rtl: _rtl, ...rest }) => (
  <button
    onClick={onClick}
    className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white border border-gray-300 rounded-full w-10 h-10 flex items-center justify-center shadow-md hover:shadow-lg transition-shadow hover:bg-gray-50"
    aria-label="Anterior"
    {...rest}>
    <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
  </button>
);

const RightArrow = ({ onClick, rtl: _rtl, ...rest }) => (
  <button
    onClick={onClick}
    className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white border border-gray-300 rounded-full w-10 h-10 flex items-center justify-center shadow-md hover:shadow-lg transition-shadow hover:bg-gray-50"
    aria-label="Siguiente"
    {...rest}>
    <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  </button>
);

const Products = ({ title, products }) => {
  const [isMobile, setIsMobile] = useState(false);

  const { getBackground, getBorder, getMuiTextStyle } = useTheme();

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const responsive = {
    superLargeDesktop: {
      breakpoint: { max: 4000, min: 3000 },
      items: 6,
    },
    desktop: {
      breakpoint: { max: 3000, min: 1024 },
      items: 4,
    },
    tablet: {
      breakpoint: { max: 1024, min: 464 },
      items: 2,
    },
    mobile: {
      breakpoint: { max: 464, min: 0 },
      items: 1,
    },
  };

  // Regla de oro precios: con variante → talla con stock; sin variante → precio directo
  const getPriceInfo = product => {
    if (!product) return { original: 0, display: 0, hasDiscount: false, pct: 0 };
    if (product.variants?.length > 0) {
      const sizes = product.variants[0]?.sizes || [];
      const size = sizes.find(s => s.stock > 0) || sizes[0];
      if (size) {
        const hasDiscount = size.discount > 0 && size.discount < size.price;
        const display = hasDiscount ? size.discount : size.price || 0;
        const original = size.price || 0;
        return { original, display, hasDiscount, pct: hasDiscount ? Math.round(((original - display) / original) * 100) : 0 };
      }
    }
    const hasDiscount = product.discount > 0 && product.discount < product.price;
    const display = hasDiscount ? product.discount : product.price || 0;
    const original = product.price || 0;
    return { original, display, hasDiscount, pct: hasDiscount ? Math.round(((original - display) / original) * 100) : 0 };
  };

  // Segunda imagen para swap al hover
  const getSecondImage = product => {
    if (!product) return null;
    if (product.variants?.length > 0) {
      const v = product.variants[0];
      if (v.colorImages?.length > 1) return v.colorImages[1];
    }
    if (product.images?.length > 1) return product.images[1];
    return null;
  };

  // Función para aplanar los productos (más robusta)
  const getFlattenedProducts = () => {
    if (!products || !Array.isArray(products)) return [];

    // Si products es un array de arrays, lo aplanamos
    if (Array.isArray(products[0])) {
      return products.flat().filter(p => p && typeof p === "object" && !Array.isArray(p));
    }

    // Si ya es un array simple, lo retornamos tal cual
    return products.filter(p => p && typeof p === "object" && !Array.isArray(p));
  };

  // Función mejorada para obtener imagen principal
  const getMainImage = product => {
    if (!product || typeof product !== "object") {
      return "https://via.placeholder.com/280x280?text=Imagen+No+Disponible";
    }

    // Primero buscamos en las variantes
    if (product.variants && Array.isArray(product.variants) && product.variants.length > 0) {
      const primeraVariante = product.variants[0];

      // Buscamos en colorImages de la variante
      if (primeraVariante.colorImages && Array.isArray(primeraVariante.colorImages) && primeraVariante.colorImages.length > 0) {
        return primeraVariante.colorImages[0];
      }

      // Buscamos en sizes con stock
      if (primeraVariante.sizes && Array.isArray(primeraVariante.sizes) && primeraVariante.sizes.length > 0) {
        const tallaConStock = primeraVariante.sizes.find(size => size && size.stock > 0);
        if (tallaConStock?.image) {
          return tallaConStock.image;
        }
        // Si no hay stock, usamos la primera talla con imagen
        const primeraTallaConImagen = primeraVariante.sizes.find(size => size && size.image);
        if (primeraTallaConImagen?.image) {
          return primeraTallaConImagen.image;
        }
      }
    }

    // Si no hay variantes, buscamos en images del producto
    if (product.images && Array.isArray(product.images) && product.images.length > 0) {
      // Si las imágenes son URLs completas, las usamos directamente
      if (typeof product.images[0] === "string" && product.images[0].startsWith("http")) {
        return product.images[0];
      }
      // Si son nombres de archivo, podrías necesitar construir la URL
      if (typeof product.images[0] === "string") {
        return `https://res.cloudinary.com/dkbwspjb7/image/upload/products/${product.images[0]}`;
      }
    }

    return "https://via.placeholder.com/280x280?text=Imagen+No+Disponible";
  };

  // Función para verificar si un producto tiene descuento
  const hasProductDiscount = product => {
    if (!product || typeof product !== "object") return false;

    // Verificar descuento directo del producto
    if (product.discount > 0) return true;

    // Verificar descuento en variantes
    if (product.variants && Array.isArray(product.variants) && product.variants.length > 0) {
      const primeraVariante = product.variants[0];
      if (primeraVariante.sizes && Array.isArray(primeraVariante.sizes)) {
        return primeraVariante.sizes.some(size => size && size.discount > 0);
      }
    }

    return false;
  };

  // Función para ordenar productos: primero los que tienen descuento
  const getSortedProducts = () => {
    const flattened = getFlattenedProducts();

    return flattened.sort((a, b) => {
      const aHasDiscount = hasProductDiscount(a);
      const bHasDiscount = hasProductDiscount(b);

      // Si ambos tienen descuento o ninguno tiene, mantener el orden original
      if (aHasDiscount === bHasDiscount) return 0;

      // Si a tiene descuento y b no, a va primero
      if (aHasDiscount && !bHasDiscount) return -1;

      // Si b tiene descuento y a no, b va primero
      return 1;
    });
  };

  const sortedProducts = getSortedProducts();

  // Validación mejorada para productos vacíos
  if (!products || sortedProducts.length === 0) {
    return (
      <div className="w-[87%] lg:w-[87%] md:w-[95%] sm:w-full mx-auto relative px-4 lg:px-20 md:px-6">
        <div className="w-full flex items-center justify-center mt-1 mb-4">
          <div className="text-center">
            <h2 className="text-3xl text-slate-600 font-bold">{title}</h2>
            <div style={{ ...getBackground("primary") }} className="w-[100px] h-[2px]  mt-4 mx-auto"></div>
          </div>
        </div>
        <div className="text-center py-8">
          <p className="text-slate-500 text-lg">No hay productos disponibles en {title}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-[87%] lg:w-[87%] md:w-[95%] sm:w-full mx-auto relative px-4 lg:px-20 md:px-6">
      {/* Título centrado verticalmente */}
      <div className="w-full flex items-center justify-center mt-1 mb-4">
        <div className="text-center">
          <h2 className="text-3xl text-slate-600 font-bold">{title}</h2>
          <div style={{ ...getBackground("primary") }} className="w-[100px] h-[2px]  mt-4 mx-auto"></div>
        </div>
      </div>

      <div className="relative max-w-full overflow-hidden px-0">
        <Carousel
          autoPlay={true}
          infinite={false}
          arrows={!isMobile}
          responsive={responsive}
          transitionDuration={500}
          containerClass="!overflow-visible"
          itemClass="px-2"
          additionalTransfrom={0}
          showDots={isMobile}
          dotListClass="flex justify-center items-center gap-1 mt-3"
          renderDotsOutside={isMobile}
          customDot={<CustomDot />}
          customLeftArrow={<LeftArrow />}
          customRightArrow={<RightArrow />}>
          {sortedProducts.map((product, index) => {
            const { original, display, hasDiscount, pct } = getPriceInfo(product);
            const mainImg   = getMainImage(product);
            const secondImg = getSecondImage(product);

            return (
              <motion.div
                key={product?._id || `product-${index}`}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, delay: index * 0.04 }}
                whileHover={{ y: -4, boxShadow: "0 12px 32px 0 rgba(174,29,34,0.18), 0 4px 12px 0 rgba(0,0,0,0.10)" }}
                className="border rounded-lg overflow-hidden"
                style={{ ...getBackground("surface"), ...getBorder("border") }}>

                <div className="relative overflow-hidden">
                  {/* Badge descuento */}
                  {hasDiscount && (
                    <div
                      style={{ ...getBackground("backgroundBadgeProduct") }}
                      className="flex justify-center items-center absolute text-white w-[38px] h-[38px] rounded-full font-semibold text-xs left-2 top-2 z-10">
                      -{pct}%
                    </div>
                  )}

                  <Link to={`/product/details/${product?.slug || "#"}`} className="block relative">
                    <div className="p-3 relative overflow-hidden group">
                      {/* Imagen principal */}
                      <img
                        src={mainImg}
                        alt={product?.name || "Producto"}
                        className="w-full object-contain transition-opacity duration-200 group-hover:opacity-0"
                        onError={e => { e.target.src = "https://via.placeholder.com/280x280?text=Imagen+No+Disponible"; }}
                      />
                      {/* Segunda imagen swap */}
                      {secondImg && (
                        <img
                          src={secondImg}
                          alt={`${product?.name} - vista 2`}
                          aria-hidden="true"
                          className="absolute top-0 left-0 w-full h-full object-contain opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-3"
                          onError={e => { e.target.style.display = 'none'; }}
                        />
                      )}
                    </div>

                    <div className="py-3 px-2">
                      <p style={getMuiTextStyle("textdescriptionProduct")} className="font-bold text-sm line-clamp-2 normal-case mb-2">
                        {product?.name || "Nombre no disponible"}
                      </p>
                      <div className="flex items-center gap-3 flex-wrap">
                        {hasDiscount ? (
                          <>
                            <span style={getMuiTextStyle("textpriceProduct")} className="text-sm leading-6 line-through">
                              S/{original.toFixed(2)}
                            </span>
                            <span style={getMuiTextStyle("textdiscountProduct")} className="font-system-ui text-lg leading-6 font-semibold">
                              S/{display.toFixed(2)}
                            </span>
                            <span style={getMuiTextStyle("textpercentdsctProduct")} className="bg-[rgba(230,0,0,0.2)] text-xs font-extrabold tracking-[0.075rem] py-1 px-2 rounded-[0.125rem]">
                              -{pct}%
                            </span>
                          </>
                        ) : (
                          <p style={getMuiTextStyle("textpriceProduct")} className="font-system-ui text-lg leading-6 font-semibold">
                            S/{display.toFixed(2)}
                          </p>
                        )}
                      </div>
                    </div>
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </Carousel>
      </div>
    </div>
  );
};

export default Products;
